import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { fulfillmentService } from '@/services/fulfillment';
import { getApiError } from '@/services/api';
import { patientIdentifier } from '@/lib/patient';
import type { FulfillmentWithRelations } from '@dpfs/shared';
import { ChevronLeft, ChevronRight, ClipboardList, ScanLine } from 'lucide-react';
import { PageHeader, EmptyState, LoadingState } from '@/components/ui';

// The history endpoint includes the prescription's patient (id, ninLast4), prescriber
// and items, which the shared Prescription type does not declare.
type HistoryEntry = FulfillmentWithRelations & {
  prescription?: FulfillmentWithRelations['prescription'] & {
    patient?: { id: string; ninLast4?: string | null } | null;
    prescriber?: { id: string; firstName: string; lastName: string } | null;
  };
};

export function FulfillmentHistory() {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const dateLocale = i18n.language === 'ar' ? 'ar-DZ' : 'fr-DZ';

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['fulfillments', page],
    queryFn: () => fulfillmentService.getHistory({ page, limit: 20 }),
    placeholderData: (prev) => prev,
  });

  const entries = (data?.data ?? []) as HistoryEntry[];
  const totalPages = data?.pagination.totalPages ?? 1;

  return (
    <div>
      <PageHeader
        title={t('nav.history')}
        description={t('pharmacist.history.description')}
        actions={
          <Link to="/pharmacist/scan" className="btn-primary">
            <ScanLine className="h-4 w-4" />
            {t('nav.scanQR')}
          </Link>
        }
      />

      {isLoading ? (
        <div className="card p-0">
          <LoadingState />
        </div>
      ) : isError ? (
        <div className="alert-danger items-center justify-between">
          <span>{getApiError(error).message}</span>
          <button type="button" onClick={() => refetch()} className="btn-secondary btn-sm">
            {t('pharmacist.common.retry')}
          </button>
        </div>
      ) : entries.length > 0 ? (
        <>
          <ul className="space-y-3">
            {entries.map((fulfillment) => {
              const rx = fulfillment.prescription;
              const itemCount = rx?.items?.length ?? 0;
              return (
                <li key={fulfillment.id}>
                  <Link
                    to={`/pharmacist/prescription/${fulfillment.prescriptionId}`}
                    className="card card-interactive group block p-4 sm:p-5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-semibold text-gray-900">
                            {t('pharmacist.common.rxNumber', { id: fulfillment.prescriptionId.slice(-8) })}
                          </h3>
                          {fulfillment.partialFulfillment ? (
                            <span className="badge-warning">{t('fulfillment.partiallyFulfilled')}</span>
                          ) : (
                            <span className="badge-success">{t('fulfillment.fulfilled')}</span>
                          )}
                        </div>
                        <p className="mt-0.5 text-sm text-gray-500">
                          {new Date(fulfillment.fulfilledAt).toLocaleString(dateLocale, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })}
                        </p>
                      </div>
                      <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-gray-300 transition-colors group-hover:text-gray-500 rtl-flip" />
                    </div>

                    {rx && (
                      <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
                        <div className="flex gap-1.5">
                          <dt className="text-gray-500">{t('prescription.patient')}:</dt>
                          <dd className="font-medium text-gray-700">
                            {rx.patient ? patientIdentifier(rx.patient).masked : '—'}
                          </dd>
                        </div>
                        <div className="flex min-w-0 gap-1.5">
                          <dt className="shrink-0 text-gray-500">{t('prescription.prescriber')}:</dt>
                          <dd className="truncate font-medium text-gray-700">
                            {rx.prescriber
                              ? t('pharmacist.common.doctorName', {
                                  name: `${rx.prescriber.firstName} ${rx.prescriber.lastName}`,
                                })
                              : '—'}
                          </dd>
                        </div>
                        {itemCount > 0 && (
                          <div className="flex gap-1.5">
                            <dt className="text-gray-500">{t('prescription.medications')}:</dt>
                            <dd className="font-medium text-gray-700">{itemCount}</dd>
                          </div>
                        )}
                      </dl>
                    )}
                    {fulfillment.notes && (
                      <p className="mt-2 break-words text-sm text-gray-600">
                        <span className="text-gray-500">{t('prescription.notes')}:</span> {fulfillment.notes}
                      </p>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>

          {totalPages > 1 && (
            <nav className="mt-6 flex items-center justify-between gap-3" aria-label={t('pharmacist.common.pagination')}>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary"
              >
                <ChevronLeft className="h-4 w-4 rtl-flip" />
                <span className="hidden sm:inline">{t('pharmacist.common.previous')}</span>
              </button>
              <span className="text-sm text-gray-600 tabular-nums">
                {t('pharmacist.common.pageOf', { page, total: totalPages })}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-secondary"
              >
                <span className="hidden sm:inline">{t('pharmacist.common.next')}</span>
                <ChevronRight className="h-4 w-4 rtl-flip" />
              </button>
            </nav>
          )}
        </>
      ) : (
        <div className="card p-0">
          <EmptyState
            icon={ClipboardList}
            title={t('pharmacist.history.empty')}
            description={t('pharmacist.history.emptyHint')}
            action={
              <Link to="/pharmacist/scan" className="btn-primary">
                <ScanLine className="h-4 w-4" />
                {t('nav.scanQR')}
              </Link>
            }
          />
        </div>
      )}
    </div>
  );
}
